import { Card, Col, Flex, Form, Input, message, Row, Table } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import PurchaseOrderService from "../../services/PurchaseOrder/PurchaseOrderService";
import CustomDateTimePicker from "../../common/CustomDateTimePicker";
import { ContainerLoadTypeSelect, ForwarderSelect, getRowClassName, PortCodeSelect } from "../../helpers/common";
import { useEffect } from "react";
import AppButton from "../../common/AppButton";
import { useGoBack } from "../../customHooks/useGoBack";
import dayjs from "dayjs";
import { queryClient } from "../../react-query-client";
import { PURCHASE_ORDER_ROOT } from "../RouteConstants";

const candidatePosColumns = [
  {
    title: "PO Number",
    dataIndex: "poNumber",
    width: 100,
  },
  {
    title: "Contents",
    dataIndex: "contentSummary",
    width: 200,
  },
];

const PurchaseOrderShipmentRequestPage = () => {
  const location = useLocation();
  const [form] = Form.useForm();
  const goBack = useGoBack();
  const navigate = useNavigate();

  let purchaseOrderId = null;
  if (location.state && location.state?.purchaseOrderId) {
    purchaseOrderId = location.state.purchaseOrderId;
  }

  const { data: currentPo } = useQuery({
    queryKey: ["purchaseOrders", purchaseOrderId],
    queryFn: async () => await PurchaseOrderService.getPurchaseOrderById(purchaseOrderId),
    enabled: !!purchaseOrderId,
  });

  const candidatePosQueryKey = [
    "purchaseOrders",
    purchaseOrderId,
    currentPo?.supplierID,
    currentPo?.warehouseID,
    currentPo?.statusID,
    "candidatePos",
  ];

  const { data: candidatePos, isLoading } = useQuery({
    queryKey: candidatePosQueryKey,
    queryFn: async () => await PurchaseOrderService.getPurchaseOrders({
      supplierID: currentPo?.supplierID,
      warehouseIds: [currentPo?.warehouseID],
      status: currentPo?.statusID,
      sortFilter: { field: "", order: "" },
      pageFilter: { all: true, size: 100, num: 0 },
    }),
    enabled: !!currentPo,
  });

  const { mutate: newShipmentRequest, isPending } = useMutation({
    mutationFn: async (request) => await PurchaseOrderService.requestShipment(request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrders", purchaseOrderId] });
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrderListTabCounts"] });
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrderList"] });
      await queryClient.invalidateQueries({ queryKey: candidatePosQueryKey });
      await message.success("Shipment requested successfully!");
      navigate(PURCHASE_ORDER_ROOT);
    },
    onError: async (error) => {
      await message.error(`Failed to request shipment. ${error.response?.data?.message}, Please try again.`);
    },
  });

  useEffect(() => {
    if (!currentPo) return;

    form.setFieldsValue({
      cargoReadyDate: currentPo.cargoReadyDate ? dayjs(currentPo.cargoReadyDate) : null,
      portOfLoading: currentPo?.portOfLoadingCode,
      portOfDischarge: currentPo?.portOfDischargeCode,
      candidatePos: [currentPo],
    });
  }, [currentPo, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      values.cargoReadyDate = values.cargoReadyDate?.format("YYYY-MM-DD");
      values.forwarder = values.forwarder.toString();
      values.candidatePos = values.candidatePos.map(po => po.id);
      values.warehouseId = currentPo?.warehouseID;

      newShipmentRequest(values);
    } catch (e) {
      // Do nothing, let the form handle the error messages
    }
  };

  const handleCancel = () => goBack();

  return (
    <div className="px-8 mb-4 adaptive-text">
      <Flex justify="space-between" align="center" className="w-full my-2  mx-2 font-inter">
        <span className="header-title">
          New Shipment Request
        </span>
      </Flex>
      <Form form={form} layout="vertical">
        <Card className="card-form flex-1 h-full">
          <Row gutter={[8, 8]} className="mb-4">
            <Col span={12} xs={24} md={24} lg={12}>
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={8}>
                  <Form.Item label="Cargo Ready Date"
                             name="cargoReadyDate"
                             rules={[{ required: true, message: "Please enter cargo ready date" }]}>
                    <CustomDateTimePicker
                      type="date"
                      form={form}
                      itemName="cargoReadyDate"
                      className="w-full h-[32px]"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Port of Loading"
                             name="portOfLoading"
                             rules={[{ required: true, message: "Please select port of loading" }]}>
                    <PortCodeSelect />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Port of Discharge"
                             name="portOfDischarge"
                             rules={[{ required: true, message: "Please select port of discharge" }]}>
                    <PortCodeSelect />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Forwarder"
                             name="forwarder"
                             rules={[{ required: true, message: "Please select forwarder" }]}>
                    <ForwarderSelect />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Container Load Type"
                             name="containerLoadType"
                             rules={[{ required: true, message: "Please select container load type" }]}>
                    <ContainerLoadTypeSelect />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col span={12} xs={24} md={24} lg={12}>
              <Form.Item label="Container Details" name="notes">
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
          <Form.List name="candidatePos">
            {(fields, { add, remove }) => {
              const selectedPos = form.getFieldValue("candidatePos") || [];
              const selectedRowKeys = selectedPos.map(po => po.id);

              return (
                <Table
                  rowKey="id"
                  dataSource={candidatePos?.data}
                  columns={candidatePosColumns}
                  pagination={false}
                  loading={isLoading || isPending}
                  rowSelection={{
                    selectedRowKeys,
                    onSelectAll: (selected, selectedRows) => {
                      if (selected) {
                        return selectedRows
                          .filter(po => po.id !== currentPo?.id)
                          .forEach(po => add(po));
                      }

                      form.setFieldValue("candidatePos", [currentPo]);
                    },
                    onSelect: (record, selected) => {
                      const idx = selectedPos.findIndex(f => f.id === record.id);
                      if (record.id === currentPo?.id) return;

                      if (selected && idx === -1) {
                        add(record);
                      }

                      if (!selected && idx > -1) {
                        remove(idx);
                      }
                    },
                  }}
                  rowClassName={getRowClassName}
                />

              );

            }}
          </Form.List>
        </Card>
        <Flex justify="end" align="center" gap={8} className="mt-4">
          <AppButton variant="save" className="button-primary" type="primary" onClick={handleSubmit}>Save</AppButton>
          <AppButton variant="close" className="button-secondary" onClick={handleCancel}>Close</AppButton>
        </Flex>
      </Form>
    </div>
  );
};

export default PurchaseOrderShipmentRequestPage;